import React, { useEffect, useRef, useState } from "react";
import { ArrowUpRight, Expand, X } from "lucide-react";

const screens = [
  {
    label: "Clinic day",
    title: "Start with the whole day in view.",
    detail: "Appointments, quick actions and the work waiting for your team, together on the dashboard.",
    image: "/images/product/dashboard.webp",
    width: 1819,
    height: 865,
    alt: "Illustrative CloudVetX dashboard showing a sample appointment queue and clinic activity",
  },
  {
    label: "Patient story",
    title: "Pick up the case where you left it.",
    detail: "History, the current visit, clinical notes, images and results stay connected to the patient.",
    image: "/images/product/patient.webp",
    width: 1536,
    height: 1024,
    alt: "Illustrative sample patient profile with visit history, notes and clinical information",
  },
  {
    label: "Client updates",
    title: "Keep the conversation close to the care.",
    detail: "See a sample WhatsApp communication history alongside the client and patient context.",
    image: "/images/product/whatsapp.webp",
    width: 1670,
    height: 942,
    alt: "Illustrative WhatsApp communication history using fictional client and pet details",
  },
  {
    label: "Invoice",
    title: "Move from visit to invoice.",
    detail: "Review the charges in a clear invoice before sharing them with a client.",
    image: "/images/product/invoice.webp",
    width: 1821,
    height: 864,
    alt: "Illustrative invoice review with sample services and amounts",
  },
  {
    label: "Stock movement",
    title: "Know what moved, and why.",
    detail: "Follow a sample report of medicines and supplies moving through the practice.",
    image: "/images/product/stock.webp",
    width: 1819,
    height: 865,
    alt: "Illustrative stock movement report populated with sample inventory activity",
  },
];

const ProductTour = () => {
  const [active, setActive] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const dialogRef = useRef(null);
  const tabRefs = useRef([]);
  const screen = screens[active];

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog || !expanded) return undefined;
    dialog.showModal();
    return () => {
      if (dialog.open) dialog.close();
    };
  }, [expanded]);

  const handleTabKey = (event, index) => {
    let next;
    if (event.key === "ArrowRight") next = (index + 1) % screens.length;
    else if (event.key === "ArrowLeft") next = (index - 1 + screens.length) % screens.length;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = screens.length - 1;
    else return;
    event.preventDefault();
    setActive(next);
    tabRefs.current[next]?.focus();
  };

  return (
    <section id="product-tour" className="cvx-tour cvx-section" aria-labelledby="cvx-tour-heading">
      <div className="container mx-auto px-5 sm:px-8">
        <div className="cvx-tour-intro">
          <div>
            <p className="cvx-kicker mb-5">Inside CloudVetX</p>
            <h2 id="cvx-tour-heading" className="cvx-section-title">A clinic day, <em>in focus.</em></h2>
          </div>
          <p>Follow the work from the morning schedule to the patient's visit, the client conversation and the details that close out the day.</p>
        </div>

        <div className="cvx-tour-layout">
          <div className="cvx-tour-nav" role="tablist" aria-label="Explore product screens">
            {screens.map((item, index) => (
              <button
                key={item.label}
                ref={(node) => { tabRefs.current[index] = node; }}
                type="button"
                id={`cvx-tour-tab-${index}`}
                role="tab"
                aria-selected={active === index}
                aria-controls="cvx-tour-panel"
                tabIndex={active === index ? 0 : -1}
                className={`cvx-tour-tab ${active === index ? "is-active" : ""}`}
                onClick={() => setActive(index)}
                onKeyDown={(event) => handleTabKey(event, index)}
              >
                <span className="cvx-tour-tab-number">0{index + 1}</span>
                <span className="cvx-tour-tab-label">{item.label}</span>
                <ArrowUpRight size={18} aria-hidden="true" className="cvx-tour-tab-arrow" />
              </button>
            ))}
          </div>

          <div id="cvx-tour-panel" role="tabpanel" aria-labelledby={`cvx-tour-tab-${active}`} className="cvx-tour-panel">
            <div className="cvx-tour-screen" key={screen.image}>
              <div className="cvx-tour-window-bar" aria-hidden="true"><span /><span /><span /><b>CloudVetX</b></div>
              <button type="button" className="cvx-tour-image-button" onClick={() => setExpanded(true)} aria-label={`Enlarge ${screen.label} preview`}>
                <img src={screen.image} alt={screen.alt} width={screen.width} height={screen.height} loading={active === 0 ? "eager" : "lazy"} />
                <span className="cvx-tour-expand"><Expand size={17} aria-hidden="true" /> Enlarge preview</span>
              </button>
            </div>
            <p className="cvx-tour-sample">Illustrative previews with sample data</p>
            <div className="cvx-tour-caption" key={screen.title}>
              <div><span className="cvx-tour-caption-index">0{active + 1} / 0{screens.length}</span><h3>{screen.title}</h3><p>{screen.detail}</p></div>
              <a href="/#contact" className="cvx-tour-link">See it in a walkthrough <ArrowUpRight size={18} aria-hidden="true" /></a>
            </div>
          </div>
        </div>
      </div>

      {expanded && (
        <dialog ref={dialogRef} className="cvx-tour-dialog" onClose={() => setExpanded(false)} onClick={(event) => { if (event.target === event.currentTarget) event.currentTarget.close(); }} aria-label={`${screen.label} enlarged preview`}>
          <div className="cvx-tour-dialog-content">
            <div className="cvx-tour-dialog-header"><span>{screen.label} · Illustrative preview with sample data</span><button type="button" onClick={() => dialogRef.current?.close()} aria-label="Close preview"><X size={21} /></button></div>
            <img src={screen.image} alt={screen.alt} width={screen.width} height={screen.height} />
          </div>
        </dialog>
      )}
    </section>
  );
};

export default ProductTour;
